(() => {
  const STATE_LABELS = {
    AGU: 'Aguascalientes', BCN: 'Baja California', BCS: 'Baja California Sur', CAM: 'Campeche', CHP: 'Chiapas',
    CHH: 'Chihuahua', CMX: 'CDMX', COA: 'Coahuila', COL: 'Colima', DUR: 'Durango', GUA: 'Guanajuato',
    GRO: 'Guerrero', HID: 'Hidalgo', JAL: 'Jalisco', MEX: 'Estado de México', MIC: 'Michoacán', MOR: 'Morelos',
    NAY: 'Nayarit', NLE: 'Nuevo León', PUE: 'Puebla', QUE: 'Querétaro', ROO: 'Quintana Roo', SIN: 'Sinaloa',
    SLP: 'San Luis Potosí', SON: 'Sonora', TAB: 'Tabasco', TAM: 'Tamaulipas', TLA: 'Tlaxcala', VER: 'Veracruz',
    YUC: 'Yucatán', ZAC: 'Zacatecas'
  };

  // Correcciones conocidas del archivo legado. Mantener aquí hasta que la fuente se limpie.
  const STORE_OVERRIDES = {
    T112: { stateCode: 'BCN' }, // Tijuana
    T117: { stateCode: 'MEX' }, // Coacalco
    T107: { stateCode: 'MEX' }  // Valle de Chalco
  };

  const normalizeText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const cleanDisplayName = (rawName = '') => {
    const parts = String(rawName).split(',').map((part) => part.trim()).filter(Boolean);
    const candidate = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '';
    return candidate
      .toLowerCase()
      .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
  };

  const normalizeZip = (zip) => String(zip ?? '').replace(/\D/g, '').padStart(5, '0').slice(-5);

  function normalizeStore(raw) {
    const storeCode = String(raw.storeCode || '').trim();
    const override = STORE_OVERRIDES[storeCode] || {};
    const latitude = Number.parseFloat(raw.lat);
    const longitude = Number.parseFloat(raw.lng);
    const stateCode = override.stateCode || String(raw.estado || '').trim().toUpperCase();

    return {
      raw,
      id: raw.id,
      storeCode,
      name: cleanDisplayName(raw.name),
      displayName: fixEncoding(raw.name),
      city: String(raw.ciudad || '').trim(),
      municipality: String(raw.municipio || '').trim(),
      address1: String(raw.address || '').trim(),
      address2: String(raw.addressTwo || '').trim(),
      stateCode,
      state: STATE_LABELS[stateCode] || stateCode,
      postalCode: normalizeZip(raw.zipCode),
      phone: raw.phone ? String(raw.phone) : '',
      website: String(raw.website || '').trim(),
      email: String(raw.email || '').trim(),
      latitude,
      longitude,
      hasValidCoordinates: Number.isFinite(latitude) && Number.isFinite(longitude),
      schedule: {
        monday: raw.monday || '', tuesday: raw.tuesday || '', wednesday: raw.wednesday || '',
        thursday: raw.thursday || '', friday: raw.friday || '', saturday: raw.saturday || '', sunday: raw.sunday || ''
      }
    };
  }

  function fixEncoding(value) {
    if (typeof value !== 'string') return value;

    if (!/[ÃÂ]/.test(value)) {
      return value;
    }

    try {
      return decodeURIComponent(
        Array.from(value)
          .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
    } catch {
      return value;
    }
  }

  function getStores() {
    const source = Array.isArray(window.shasaStores) ? window.shasaStores : [];
    return source.map(normalizeStore).filter((store) => store.storeCode && store.hasValidCoordinates);
  }

  function haversineKm(a, b) {
    const R = 6371;
    const toRad = (deg) => deg * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function storesNear(coords, radiusKm = Infinity) {
    return getStores()
      .map((store) => ({ ...store, distanceKm: haversineKm(coords, { lat: store.latitude, lng: store.longitude }) }))
      .filter((store) => store.distanceKm <= radiusKm)
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }

  function storesByState(stateCode) {
    return getStores().filter((store) => store.stateCode === stateCode);
  }

  function searchStores(query, stores = getStores()) {
    const needle = normalizeText(query);
    if (!needle) return stores;
    return stores.filter((store) => normalizeText([
      store.displayName, store.name, store.city, store.municipality, store.address1, store.address2,
      store.state, store.stateCode, store.postalCode, store.storeCode
    ].join(' ')).includes(needle));
  }

  function uniqueStates() {
    const map = new Map();
    getStores().forEach((store) => map.set(store.stateCode, store.state));
    return [...map.entries()].map(([code, label]) => ({ code, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }

  async function geocodePostalCode(postalCode) {
    const cp = normalizeZip(postalCode);
    if (!/^\d{5}$/.test(cp)) throw new Error('INVALID_POSTAL_CODE');
    if (!window.google?.maps?.Geocoder) throw new Error('GOOGLE_MAPS_NOT_READY');

    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ address: `${cp}, México`, componentRestrictions: { country: 'MX', postalCode: cp } });
    const result = results?.[0];
    if (!result?.geometry?.location) throw new Error('POSTAL_CODE_NOT_FOUND');
    return { lat: result.geometry.location.lat(), lng: result.geometry.location.lng(), postalCode: cp };
  }

  function requestUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(Object.assign(new Error('GEOLOCATION_UNSUPPORTED'), { code: 0 }));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
        reject,
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  function getConfig() {
    return window.ShasaStoreLocatorConfig || {};
  }

  async function api(path, payload) {
    const config = getConfig();
    const base = (config.apiBase || 'https://social.samaya.mx/api/shasa/v4').replace(/\/$/, '');
    const response = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.status === 'error') {
      const error = new Error(data?.message || `HTTP_${response.status}`);
      error.response = data;
      throw error;
    }
    return data;
  }

  function updateStoreHeaderLabel(storeCode, storeData = null) {
    const labels = document.querySelectorAll(
      '[data-store-header-label]'
    );

    if (!labels.length) return;

    let label = '';

    /*
     * Primero intentamos usar el nombre devuelto por backend.
     */
    if (storeData) {
      label =
        storeData.displayName ||
        storeData.name ||
        storeData.store_name ||
        '';
    }

    /*
     * Si backend no trae nombre o su estructura cambia,
     * buscamos la tienda en shasa-stores.js por storeCode.
     */
    if (!label && storeCode) {
      const localStore = getStores().find(
        (store) => store.storeCode === storeCode
      );

      if (localStore) {
        label =
          localStore.name ||
          localStore.displayName ||
          '';
      }
    }

    if (!label) return;

    labels.forEach((element) => {
      element.textContent = label;
    });
  }

  async function getFavoriteStore() {
    const { customerId } = getConfig();

    if (!customerId) {
      return null;
    }

    try {
      const response = await api(
        '/api/shasa/v4/customer/get-store',
        {
          customer_shopify_id:
            String(customerId)
        }
      );

      const data = response?.data;

      if (!data?.store_code) {
        return null;
      }

      const favorite = {
        customerId:
          data.customer_shopify_id,

        storeCode:
          data.store_code,

        shopifyLocationId:
          data.shopify_location_id,

        shopifyLocationGid:
          data.shopify_location_gid,

        store:
          data.store || null
      };

      /*
       * Actualizamos el header con la favorita real.
       */
      updateStoreHeaderLabel(
        favorite.storeCode,
        favorite.store
      );

      return favorite;
    } catch (error) {
      /*
       * Customer sin favorita:
       * simplemente dejamos el texto original del header.
       */
      console.warn(
        '[StoreLocator] Could not load favorite store:',
        error
      );

      return null;
    }
  }

  async function saveFavoriteStore(storeCode) {
    const { customerId } = getConfig();
    if (!customerId) throw new Error('CUSTOMER_REQUIRED');
    return api('/api/shasa/v4/customer/store', {
      customer_shopify_id: String(customerId),
      store_code: storeCode
    });
  }

  async function syncCartPreference() {
    const { customerId } = getConfig();
    if (!customerId) return { skipped: 'guest' };

    const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('CART_FETCH_FAILED');
    const cart = await cartResponse.json();
    if (!cart.item_count) return { skipped: 'empty_cart' };
    if (!cart.token) throw new Error('CART_TOKEN_MISSING');

    // Decisión de implementación solicitada: tratar cart.token como cart_id.
    return api('/api/shasa/v4/cart/update-buyer-identity', {
      customer_shopify_id: String(customerId),
      cart_id: cart.token
    });
  }

  function mapUrl(context = {}) {
    const { locatorUrl = '/pages/localizador-de-tiendas' } = getConfig();
    const params = new URLSearchParams();
    Object.entries(context).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    return `${locatorUrl}${params.size ? `?${params}` : ''}`;
  }

  window.ShasaStoreLocator = {
    STATE_LABELS, normalizeStore, getStores, storesNear, storesByState, searchStores, uniqueStates,
    geocodePostalCode, requestUserLocation, getFavoriteStore, saveFavoriteStore, syncCartPreference, mapUrl,
    normalizeZip
  };

  function loadFavoriteStoreForHeader() {
    const { customerId } = getConfig();

    if (!customerId) return;

    getFavoriteStore().catch((error) => {
      console.warn(
        '[StoreLocator] Header favorite store load failed:',
        error
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      loadFavoriteStoreForHeader,
      {
        once: true
      }
    );
  } else {
    loadFavoriteStoreForHeader();
  }

  /*
 * CART DRAWER INTEGRATION
 *
 * Cuando Broadcast abre el cart drawer:
 *
 * theme:cart-drawer:open
 *      ↓
 * syncCartPreference()
 *      ↓
 * GET /cart.js
 *      ↓
 * si hay productos:
 * POST /v4/cart/update-buyer-identity
 */

  let cartPreferenceSyncInFlight = false;

  document.addEventListener(
    'theme:cart-drawer:open',
    async () => {
      console.log(
        '[StoreLocator] Cart drawer opened'
      );

      /*
       * Guest:
       * no puede tener tienda favorita.
       */
      if (
        !window.ShasaStoreLocatorConfig
          ?.customerId
      ) {
        console.log(
          '[StoreLocator] Skip cart sync: customer not logged in'
        );

        return;
      }

      /*
       * Defensa para no ejecutar dos requests
       * simultáneos si el drawer dispara más de
       * un evento durante alguna actualización.
       */
      if (cartPreferenceSyncInFlight) {
        console.log(
          '[StoreLocator] Cart sync already running'
        );

        return;
      }

      if (
        !window.ShasaStoreLocator
          ?.syncCartPreference
      ) {
        console.warn(
          '[StoreLocator] syncCartPreference is not available'
        );

        return;
      }

      cartPreferenceSyncInFlight = true;

      try {
        const result =
          await window.ShasaStoreLocator
            .syncCartPreference();

        console.log(
          '[StoreLocator] Cart preference synced',
          result
        );
      } catch (error) {
        console.error(
          '[StoreLocator] Cart preference sync failed:',
          error
        );
      } finally {
        cartPreferenceSyncInFlight = false;
      }
    }
  );
})();
